import { UserRoles } from '@dynamika-shared/types/auth';
import { routesPath } from '@router/routes';

const roleRouteMapping = {
  [UserRoles.READER]: {
    taskViewer: routesPath.readerTaskViewer,
    subTaskViewer: routesPath.readerSubTaskViewer,
    taskViewerDetached: routesPath.readerTaskViewerDetached,
    subTaskViewerDetached: routesPath.readerSubTaskViewerDetached,
    taskPlain: routesPath.readerTaskPlain,
  },
  [UserRoles.SPONSOR]: {
    taskViewer: routesPath.sponsorTaskViewer,
    subTaskViewer: routesPath.sponsorSubTaskViewer,
    taskViewerDetached: routesPath.sponsorTaskViewerDetached,
    subTaskViewerDetached: routesPath.sponsorSubTaskViewerDetached,
    taskPlain: routesPath.sponsorTaskPlain,
  },
  [UserRoles.PROJECT_MANAGER]: {
    taskViewer: routesPath.clinicalTrialTaskViewer,
    subTaskViewer: routesPath.clinicalTrialSubTaskViewer,
    taskViewerDetached: routesPath.clinicalTrialTaskViewerDetached,
    subTaskViewerDetached: routesPath.clinicalTrialSubTaskViewerDetached,
    taskPlain: routesPath.clinicalTrialTaskPlain,
  },
  [UserRoles.ADMIN]: {
    taskViewer: routesPath.clinicalTrialTaskViewer,
    subTaskViewer: routesPath.clinicalTrialSubTaskViewer,
    taskViewerDetached: routesPath.clinicalTrialTaskViewerDetached,
    subTaskViewerDetached: routesPath.clinicalTrialSubTaskViewerDetached,
    taskPlain: routesPath.clinicalTrialTaskPlain,
  },
  [UserRoles.SUPPORT]: {
    taskViewer: routesPath.clinicalTrialTaskViewer,
    subTaskViewer: routesPath.clinicalTrialSubTaskViewer,
    taskViewerDetached: routesPath.clinicalTrialTaskViewerDetached,
    subTaskViewerDetached: routesPath.clinicalTrialSubTaskViewerDetached,
    taskPlain: routesPath.clinicalTrialTaskPlain,
  },
  [UserRoles.EXTERNAL_USER]: {
    taskViewer: routesPath.externalUserTaskViewer,
    subTaskViewer: routesPath.externalUserSubTaskViewer,
    taskViewerDetached: routesPath.externalUserTaskViewerDetached,
    subTaskViewerDetached: routesPath.externalUserSubTaskViewerDetached,
    taskPlain: routesPath.externalUserTaskPlain,
  },
} as const;

export type RouteType = keyof (typeof roleRouteMapping)[UserRoles.READER];

export const getRouteByRole = (
  role: UserRoles,
  routeType: RouteType
): string => {
  const roleMapping = roleRouteMapping[role as keyof typeof roleRouteMapping];

  if (!roleMapping) {
    return roleRouteMapping[UserRoles.SPONSOR][routeType];
  }

  return roleMapping[routeType];
};

export const getAllRoutesByRole = (role: UserRoles) => {
  return (
    roleRouteMapping[role as keyof typeof roleRouteMapping] ||
    roleRouteMapping[UserRoles.SPONSOR]
  );
};

export const isRouteForRole = (
  role: UserRoles,
  currentPath: string
): boolean => {
  const roleRoutes = getAllRoutesByRole(role);
  return Object.values(roleRoutes).some((route: string) =>
    currentPath.includes(route)
  );
};

export const selectDetachedRoute = (
  hasSubTask: boolean,
  userRole?: string
): string => {
  const role = userRole as UserRoles;

  switch (role) {
    case UserRoles.SPONSOR:
      return hasSubTask
        ? routesPath.sponsorSubTaskViewerDetached
        : routesPath.sponsorTaskViewerDetached;
    case UserRoles.PROJECT_MANAGER:
    case UserRoles.ADMIN:
    case UserRoles.SUPPORT:
      return hasSubTask
        ? routesPath.clinicalTrialSubTaskViewerDetached
        : routesPath.clinicalTrialTaskViewerDetached;
    case UserRoles.EXTERNAL_USER:
      return hasSubTask
        ? routesPath.externalUserSubTaskViewerDetached
        : routesPath.externalUserTaskViewerDetached;
    case UserRoles.READER:
    default:
      return hasSubTask
        ? routesPath.readerSubTaskViewerDetached
        : routesPath.readerTaskViewerDetached;
  }
};
